//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import styled from 'styled-components'

import { getValidator } from '../utils'
import { customIsEqual } from './utils'
import colors from 'components/utils/colors'

import TableFormGroupHeader from 'components/Shared/TableForm/TableFormGroupHeader'
import Table from 'components/Shared/TableNew/Index'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import TableCellWithActions from 'components/Shared/TableNew/TableCellWithActions'
import TableCellAction from 'components/Shared/TableNew/TableCellAction'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import IconLink from 'components/Shared/IconLink/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import TextField from './TextField'
import SwitchField from './SwitchField'
import SelectWithBadgesField from './SelectWithBadgesField'
import CreatableArrayField from './CreatableArrayField'

const StyledTableRow = styled.tr`
  border-left: 1px solid transparent;
  border-right: 1px solid transparent;
`

const StyledTableCell = styled.td`
  padding-top: 0;
`

const ValuesTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const IconHolder = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
`

const HeaderCellWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledRequiredIcon = styled.div`
  background-color: ${colors.alert};
  border-radius: 50%;
  width: 8px;
  min-width: 8px;
  height: 8px;
  margin-right: 4px;
`

const fieldComponentsByTypes = {
  text: TextField,
  switch: SwitchField,
  selectWithBadges: SelectWithBadgesField,
  creatable_array: CreatableArrayField,
}

const getDefaultFieldValues = (columns) => {
  const defaultValues = {}
  columns.forEach(column => {
    if (column.type === 'switch') {
      defaultValues[column.name] = false
    } else if (column.type === 'creatable_array' || column.type === 'selectWithBadges') {
      defaultValues[column.name] = []
    } else {
      defaultValues[column.name] = ''
    }
  })
  return defaultValues
}

const ValuesTable = ({ fields, columns, onRemoveItem, isInModal }) => {
  const { t } = useTranslation()

  const renderField = (field) => {
    const Component = fieldComponentsByTypes[field.type] || TextField
    const field_ = { ...field }
    field_.validate = (value) => getValidator(value, field_.required, field_.validateFunc)
    field_.isInModal = isInModal

    return <Component {...field_} />
  }

  return (
    <Table width='100%'>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column.name}>
              <HeaderCellWrapper>
                {column.required && <StyledRequiredIcon data-test='required-icon' />}
                {column.label}
              </HeaderCellWrapper>
            </TableCell>
          ))}
          <TableCell />
        </TableRow>
      </TableHead>

      <TableBody hoverable>
        {fields.map((namePrefix, rowIndex) => {
          const tooltipRowId = `editable-table-tooltip-${name}`

          return (
            <TableRow key={rowIndex}>
              {columns.map((column) => {
                const name = namePrefix + '.' + column.name
                const field = {
                  ...column,
                  name: name,
                }
                return (
                  <TableCell key={column.name}>
                    {renderField(field)}
                  </TableCell>
                )
              })}

              <TableCellWithActions>
                <TableCellAction
                  alwaysShow
                  iconName='icon-remove-16'
                  onClick={() => onRemoveItem(rowIndex)}
                  tooltip={t('tableFormFieldsBuilder.editableArrayField.tooltipRemove')}
                  tooltipId={tooltipRowId}
                />
                <Tooltip id={tooltipRowId} />
              </TableCellWithActions>
            </TableRow>
          )
        })}
        <TableRowNoData data={fields.value} />

      </TableBody>
    </Table>
  )
}

ValuesTable.propTypes = {
  fields: PropTypes.object,
  columns: PropTypes.array,
  onRemoveItem: PropTypes.func,
  isInModal: PropTypes.bool,
}

const EditableArrayField = ({ name, isInModal, label, indexInGroup, columns }) => {
  const form = useForm()
  const { t } = useTranslation()
  const tooltipId = `editable-table-tooltip-${name}`

  const onAddItem = (fields) => {
    const defaultValues = getDefaultFieldValues(columns)
    fields.push(defaultValues)
  }

  const onRemoveItem = (fields, rowIndex) => {
    const isLastRow = Boolean(fields.value.length === 1)
    if (isLastRow) {
      // otherwise the resulting value will be undefined
      form.change(name, [])
    } else {
      fields.remove(rowIndex)
    }
  }

  return (
    <>
      <TableFormGroupHeader
        fieldGroup={{ groupTitle: label, group: label }}
        isFirst={indexInGroup === 0}
        isInModal={isInModal}
      />
      <StyledTableRow>
        <StyledTableCell colSpan='100%'>
          <FieldArray
            name={name}
            isEqual={customIsEqual}
          >
            {({ fields }) => {
              return (
                <ValuesTableWrapper>
                  <IconHolder>
                    <Tooltip id={tooltipId} />
                    <IconLink
                      data-tooltip-content={t('tableFormFieldsBuilder.editableArrayField.tooltipAdd')}
                      data-tooltip-id={tooltipId}
                      iconName='icon-add-big-16'
                      onClick={() => onAddItem(fields)}
                      text={t('tableFormFieldsBuilder.editableArrayField.addButtonLabel')}
                    />
                  </IconHolder>

                  <ValuesTable
                    name={name}
                    fields={fields}
                    columns={columns}
                    onRemoveItem={(rowIndex) => onRemoveItem(fields, rowIndex)}
                    isInModal={isInModal}
                  />
                </ValuesTableWrapper>
              )
            }}
          </FieldArray>
        </StyledTableCell>
      </StyledTableRow>
    </>
  )
}

EditableArrayField.propTypes = {
  name: PropTypes.string,
  isInModal: PropTypes.bool,
  label: PropTypes.string,
  columns: PropTypes.array,
  indexInGroup: PropTypes.number,
}

export default EditableArrayField
